import React, { useState, useCallback } from "react";
import { Home as HomeIcon, Globe, Plane, Check, Loader2 } from "lucide-react";
import { api } from "../lib/api";

/**
 * ServiceScopeWidget — inline checkbox row letting concierges flip their
 * three service-scope flags directly from the dashboard. Activates the
 * pills that appear on their public profile (Phase 53 visual layer).
 *
 * Each toggle persists immediately via POST /providers/update, with
 * optimistic UI + a 1s success indicator. Real concierges only — preview
 * accounts get a read-only view because POST is preview-guarded.
 *
 * Sensible legacy defaults: serves_locally=true, remote_available=true,
 * travel_coordination=false (matching the public profile defaults).
 */
const FLAGS = [
  {
    key: "serves_locally",
    label: "Local Support",
    desc: "In-person help near your city",
    icon: HomeIcon,
    accent: "text-[#064E3B] bg-[#064E3B]/8",
    accentOn: "bg-[#064E3B] text-white",
  },
  {
    key: "remote_available",
    label: "Remote Planning",
    desc: "Plan and coordinate without showing up",
    icon: Globe,
    accent: "text-[#0277A5] bg-[#00AEEF]/10",
    accentOn: "bg-[#0277A5] text-white",
  },
  {
    key: "travel_coordination",
    label: "Travel Coordination",
    desc: "Arrange domestic and overseas trips for clients",
    icon: Plane,
    accent: "text-[#8A6E2F] bg-[#C9A45C]/15",
    accentOn: "bg-[#8A6E2F] text-white",
  },
];

export default function ServiceScopeWidget({ provider, onSaved }) {
  // Optimistic overlay map — only keys the user has toggled in this session.
  // Displayed state = provider value (default-true for local/remote, false
  // for travel) UNLESS the user has just flipped it, in which case the
  // overlay wins until the parent refetches. This avoids a useEffect-driven
  // setState (which the linter flags) while still feeling instantaneous.
  const [pending, setPending] = useState({});
  const [savingKey, setSavingKey] = useState(null);
  const [savedKey, setSavedKey] = useState(null);
  const [error, setError] = useState("");
  const readOnly = !!provider?.preview_account;

  const baseScope = {
    serves_locally: provider?.serves_locally !== false,
    remote_available: provider?.remote_available !== false,
    travel_coordination: Boolean(provider?.travel_coordination),
  };
  const scope = {
    serves_locally: pending.serves_locally ?? baseScope.serves_locally,
    remote_available: pending.remote_available ?? baseScope.remote_available,
    travel_coordination: pending.travel_coordination ?? baseScope.travel_coordination,
  };

  const toggle = useCallback(async (key) => {
    if (readOnly || savingKey) return;
    const next = !scope[key];
    setPending((p) => ({ ...p, [key]: next }));
    setSavingKey(key);
    setError("");
    try {
      await api.post("/providers/update", { [key]: next });
      setSavedKey(key);
      onSaved?.();
      setTimeout(() => setSavedKey((k) => (k === key ? null : k)), 1100);
    } catch (e) {
      // Roll back the optimistic overlay on failure.
      setPending((p) => {
        const { [key]: _drop, ...rest } = p;
        return rest;
      });
      setError("Couldn't save — try again.");
    } finally {
      setSavingKey(null);
    }
  }, [readOnly, savingKey, scope, onSaved]);

  return (
    <div
      data-testid="service-scope-widget"
      className="rounded-2xl border border-stone-200/80 bg-white/95 p-5 sm:p-6 shadow-[0_8px_24px_-16px_rgba(7,26,45,0.18)]"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[#C9A45C]">
            Concierge Reach
          </div>
          <h3 className="font-serif text-xl text-[#071A2D] mt-1">How you help customers</h3>
          <p className="text-[13px] text-[#071A2D]/60 mt-1 max-w-md">
            Define the ways you support clients. These appear on your public
            profile and shape the briefs you receive.
          </p>
        </div>
        {readOnly && (
          <span className="text-[11px] uppercase tracking-wider text-[#071A2D]/50 mt-1">
            Preview · read-only
          </span>
        )}
      </div>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
        {FLAGS.map(({ key, label, desc, icon: Icon, accent, accentOn }) => {
          const on = scope[key];
          const isSaving = savingKey === key;
          const isJustSaved = savedKey === key;
          return (
            <button
              key={key}
              type="button"
              role="switch"
              aria-checked={on}
              data-testid={`scope-toggle-${key}`}
              onClick={() => toggle(key)}
              disabled={readOnly || isSaving}
              className={`relative text-left rounded-xl border p-3.5 transition group disabled:cursor-not-allowed
                          ${on
                            ? "border-[#064E3B]/30 bg-[#FFF8EE] shadow-[0_6px_16px_-12px_rgba(6,78,59,0.45)]"
                            : "border-stone-200 bg-white hover:border-stone-300"}`}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 transition
                              ${on ? accentOn : accent} ${readOnly ? "" : "group-hover:scale-[1.05]"}`}
                >
                  <Icon size={16} strokeWidth={2} />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[14px] font-semibold text-[#071A2D]">{label}</span>
                    {on && (
                      <span data-testid={`scope-on-${key}`} className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#064E3B] text-white font-bold">
                        On
                      </span>
                    )}
                  </div>
                  <div className="text-[12px] text-[#071A2D]/60 mt-0.5 leading-snug">
                    {desc}
                  </div>
                </div>
              </div>
              {/* Saving / saved indicator pinned bottom-right of the card */}
              <div className="absolute top-2.5 right-2.5">
                {isSaving && <Loader2 size={13} className="animate-spin text-[#071A2D]/40" />}
                {isJustSaved && (
                  <span data-testid={`scope-saved-${key}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#064E3B] font-semibold">
                    <Check size={11} strokeWidth={3} /> Saved
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {error && (
        <div role="alert" data-testid="scope-error" className="mt-3 text-[12px] text-[#B45309]">
          {error}
        </div>
      )}
    </div>
  );
}
